import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, Info } from "lucide-react";

import { Alert, Button, Card, LogoMark, Stack } from "~/components";
import type { AlertVariant } from "~/components";
import { beginLogin } from "~/lib/auth";

import * as css from "./login.css";

// Login page. Unauth users land here either directly, because the
// `_app` route guard redirected them with a returnTo search param, or
// because the OIDC callback hit an error and 302'd them here with an
// `error` code so the failure surfaces in the SPA instead of stranding
// the user on a raw mayfirm-api URL.
//
// Clicking "Sign in with Google" kicks off beginLogin, which fetches
// the authorization URL from the API and does a full-page navigation
// to accounts.google.com. After Google + the callback set the
// cookies, the user ends up back at returnTo already authenticated.
export const Route = createFileRoute("/login")({
  component: LoginPage,
  // The guard on _app stuffs the original pathname into search.returnTo
  // when it redirects here. The callback handler stuffs ?error=<code>
  // when OIDC fails. Validate both as strings; default to "/" and
  // undefined respectively.
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    const returnTo =
      typeof search["returnTo"] === "string" ? search["returnTo"] : "/";
    const errorCode =
      typeof search["error"] === "string" ? search["error"] : undefined;
    return { returnTo, error: errorCode };
  },
});

interface LoginSearch {
  returnTo: string;
  error?: string | undefined;
}

// errorMessages maps the short codes the API (or the SPA itself, for
// session_expired (TMF-169)) emits into user-facing copy, each with the
// Alert variant it reads as. Anything we don't recognize falls back to the
// "unknown" bucket: the API never surfaces raw server errors here, so a
// miss just means "we deployed a new code the SPA hasn't learned yet"
// rather than "something dangerous is leaking through."
const errorMessages: Record<string, { message: string; variant: AlertVariant }> = {
  invalid_login: {
    message: "Your sign-in session expired or couldn't be verified. Please try again.",
    variant: "danger",
  },
  bad_request: {
    message: "The sign-in link was malformed. Please start the sign-in flow again.",
    variant: "danger",
  },
  hd_mismatch: {
    message:
      "Your Google account's domain is not authorized to sign in to this environment. Use an approved Google Workspace account or ask an admin to add your domain.",
    variant: "danger",
  },
  not_provisioned: {
    message: "Your account hasn't been provisioned yet. Ask an admin to invite you before signing in.",
    variant: "danger",
  },
  account_conflict: {
    message: "This Google account is already linked to a different user. Contact an admin to resolve the conflict.",
    variant: "danger",
  },
  // Not a failure of the rep's: the browser's own token renewal 401'd after
  // the page was already up (TMF-169), so this reads as informational rather
  // than an alarm.
  session_expired: {
    message: "Your session expired. Sign in again to pick up where you left off.",
    variant: "info",
  },
  unknown: {
    message: "Something went wrong signing you in. Please try again, and if it keeps happening contact an admin.",
    variant: "danger",
  },
};

function LoginPage() {
  const { returnTo, error } = Route.useSearch();

  const handleSignIn = () => {
    // Fire and forget: beginLogin does a full-page navigation on
    // success, and any failure throws and bubbles up to the nearest
    // React error boundary. No need to await.
    void beginLogin(returnTo);
  };

  const errorEntry =
    error !== undefined ? (errorMessages[error] ?? errorMessages["unknown"]) : undefined;

  return (
    <main className={css.page}>
      <div className={css.card}>
        <div className={css.logoBlock}>
          <LogoMark size={48} />
          <div style={{ textAlign: "center" }}>
            <h1 className={css.logoTitle}>THE MAY FIRM</h1>
            <div className={css.logoSub}>Operating System</div>
          </div>
        </div>
        <Card>
          <Stack gap="md">
            <h2>Sign in</h2>
            <p>Sign in with your Mayfirm Google Workspace account.</p>
            {errorEntry !== undefined && (
              <Alert
                variant={errorEntry.variant}
                icon={
                  errorEntry.variant === "danger" ? (
                    <AlertTriangle size={18} />
                  ) : (
                    <Info size={18} />
                  )
                }
              >
                {errorEntry.message}
              </Alert>
            )}
            <Button type="button" variant="primary" onClick={handleSignIn}>
              Sign in with Google
            </Button>
          </Stack>
        </Card>
      </div>
    </main>
  );
}
